// src/pages/checkout.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import TopBar from "../components/checkout/TopBar";
import OrderSummary from "../components/checkout/OrderSummary";
import LoaderOverlay from "../components/checkout/LoaderOverlay";
import CheckoutForm from '../components/checkout/CheckoutForm';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from "axios";


const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function Checkout() {
  /* ---------------- GLOBAL STATE ---------------- */
  const [currency, setCurrency] = useState(null);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Recalculating the order in the background (shipping / address / promo).
  // Deliberately NOT the blocking loader: LoaderOverlay covers the whole screen,
  // so showing it here swallowed taps on the Stripe address autocomplete while a
  // quote was in flight — noticeable on slower connections and on tablets/phones.
  const [isQuoting, setIsQuoting] = useState(false);
  const [country, setCountry] = useState("US");
  const [completeAddress, setCompleteAddress] = useState(null);
  const [baseProduct, setBaseProduct] = useState(0);
  const [originalProductAmount, setOriginalProductAmount] = useState(0);
  const [subProduct, setSubProduct] = useState(0);
  const [promoCode, setPromoCode] = useState("");
  const [appliedCoupons, setAppliedCoupons] = useState([]);
  const [shipping, setShipping] = useState(Number(process.env.REACT_APP_SHIPPING_USA) || 0);
  const [taxes, setTaxes] = useState(0);
  const [total, setTotal] = useState(0);
  const [originalBaseProduct, setOriginalBaseProduct] = useState(0);
  const [originalSubProduct, setOriginalSubProduct] = useState(0);
  const [shopifyProductId, setShopifyProductId] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [pricingTier, setPricingTier] = useState(null);
  const [originalMainPrice, setOriginalMainPrice] = useState(null);
  const [originalSubPrice, setOriginalSubPrice] = useState(null);
  const [mainPriceProductInfo, setMainPriceProductInfo] = useState(null);
  const [hasPromotekitReferral, setHasPromotekitReferral] = useState(false);
  const [skipShipping, setSkipShipping] = useState(false);
  const [isfreeMemberShip, setIsfreeMemberShip] = useState(false);
  const [isfreeMemberShipMonthy, setIsfreeMemberShipMonthy] = useState(false);
  const [isfreeAccessory, setIsfreeAccessory] = useState(false);
  const [maxUsageReached, setMaxUsageReached] = useState(false);
  const [allAgreed, setAllAgreed] = useState(false);
  const [migratedCust, setMigratedCust] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const sid = searchParams.get('sid') || new URLSearchParams(window.location.search).get('sid');
  const urlEmail = searchParams.get('email') || new URLSearchParams(window.location.search).get('email') || '';

  // Simple email validation regex
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // If sid matches the free product SID and we have a valid email, auto-fill it
  const sidFreeProduct = process.env.REACT_APP_SID_FREE_PRODUCT || process.env.SID_FREE_PRODUCT || '';
  const isFreeSid = sid === sidFreeProduct;
  const defaultEmail = (isFreeSid && urlEmail && isValidEmail(urlEmail)) ? urlEmail : '';
  const urlEmailError = (isFreeSid && urlEmail && !isValidEmail(urlEmail)) ? "Invalid email format in URL." : '';

  // Use ref to prevent concurrent updates and avoid infinite loops
  const isUpdatingRef = useRef(false);
  const appliedCouponsRef = useRef(appliedCoupons);
  
  // Update ref when appliedCoupons changes
  useEffect(() => {
    appliedCouponsRef.current = appliedCoupons;
  }, [appliedCoupons]);

  // Fetch cookies from backend on component mount
  // Update shipping when country changes
  useEffect(() => {
    const shippingCost =
      country === "US"
        ? Number(process.env.REACT_APP_SHIPPING_USA)
        : Number(process.env.REACT_APP_SHIPPING_OTHER);
    
    const newShipping = shippingCost || 0;
    setShipping(newShipping);
  }, [country]);

  // Handler for when country changes from CheckoutForm
  const handleCountryChange = useCallback((newCountry) => {
    setCountry(newCountry);
  }, []);

  // Build the payload describing the current order. Used both to quote the
  // order while the customer fills the form and to create the PaymentIntent
  // when they pay, so both always price the same thing.
  const buildOrderPayload = useCallback((appliedPromoCodes) => {
    // Ensure we use the current country value from state
    // This handles the case where user changes country dropdown but address fields remain the same
    const addressToSend = completeAddress ? {
      ...completeAddress,
      country: country // Override with current country state
    } : null;

    return {
      sid: sid || undefined,
      slug: slug,
      baseProduct: baseProduct,
      shippingAmount: shipping,
      completeAddress: addressToSend,
      promoCodes: appliedPromoCodes,
      pricingTier: pricingTier || undefined,
      allAgreed: allAgreed,
      // Captured only for the free-product SID so the customer's plan choice is
      // submitted with the order. Backend pricing/totals are unchanged.
      ...(isFreeSid ? { plan: selectedPlan } : {}),
    };
  }, [sid, slug, baseProduct, shipping, completeAddress, country, pricingTier, allAgreed, selectedPlan, isFreeSid]);

  const updatePaymentIntent = useCallback(async ({ promoCode: nextPromoCode, promoCodes: nextPromoCodes } = {}) => {
    // Applying/removing a discount code is user initiated: since background
    // recalculations no longer block the UI, one may be in flight. Wait for it
    // to finish instead of dropping the customer's request.
    const isUserInitiated = Array.isArray(nextPromoCodes) || nextPromoCode !== undefined;
    if (isUserInitiated && isUpdatingRef.current) {
      for (let i = 0; i < 40 && isUpdatingRef.current; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    // Prevent concurrent updates to avoid infinite loops
    if (!checkoutReady || isUpdatingRef.current) {
      return { success: false, error: isUpdatingRef.current ? "Update in progress" : "Checkout not ready" };
    }
    
    try {
      isUpdatingRef.current = true;
      // Support multiple coupon codes - prioritize promoCodes array, fallback to single promoCode
       // Respect explicitly provided promo codes. Only fall back to ref when no promo input was given.
       let appliedPromoCodes;
       if (Array.isArray(nextPromoCodes)) {
         appliedPromoCodes = nextPromoCodes;
       } else if (nextPromoCode !== undefined) {
         appliedPromoCodes = nextPromoCode ? [nextPromoCode] : [];
       } else {
         appliedPromoCodes = appliedCouponsRef.current.map(c => c.code);
       }
      
      setIsQuoting(true);
      // Quotes the order (shipping, promo codes, tax) without creating a
      // PaymentIntent — that only happens when the customer pays.
      const resp = await axios.post(
        `${BASE_URL}/checkout/quote-intent`,
        buildOrderPayload(appliedPromoCodes)
      );
      const data = resp.data || {};
      // Update state based on backend response
      if (data.automatic_tax !== undefined) setTaxes(data.automatic_tax);
      if (data.amount) setTotal(data.amount);
      if (data.subAmount) setSubProduct(data.subAmount);
      if (data.originalSubProductPrice) setOriginalSubPrice(data.originalSubProductPrice);
      if (data.originalProductAmount) setOriginalProductAmount(data.originalProductAmount);
      if (data.discountedBaseAmount) setBaseProduct(data.discountedBaseAmount);
      setSkipShipping(!!data.skipShipping);
      setIsfreeMemberShip(!!data.isfreeMemberShip);
      setIsfreeMemberShipMonthy(!!data.isfreeMemberShipMonthy);
      setIsfreeAccessory(!!data.isfreeAccessory);
      setMaxUsageReached(!!data.maxUsageReached);
      if (data.migratedCust) setMigratedCust(data.migratedCust);
      // Update applied coupons state from backend response
      if (data.appliedCoupons && Array.isArray(data.appliedCoupons)) {
        setAppliedCoupons(data.appliedCoupons);
      } else if (!data.success) {
        // On failure, clear applied coupons
        setAppliedCoupons([]);
      } 
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err };
    } finally {
      setIsQuoting(false);
      isUpdatingRef.current = false;
    }
  }, [checkoutReady, buildOrderPayload]);

  // Update PaymentIntent when address or shipping changes
  // This handles: 1) Tax calculation when address is complete, 2) Shipping cost updates
  useEffect(() => {
    // Need the checkout pricing to be loaded before making any updates
    if (!checkoutReady) return;
    
    // Debounce to ensure state has settled, especially when multiple states update together
    const timeoutId = setTimeout(() => {
      updatePaymentIntent();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [completeAddress, shipping, checkoutReady, updatePaymentIntent, selectedPlan]);
  
  // Load the pricing for this checkout URL on mount.
  // NOTE: this deliberately does NOT create a PaymentIntent — opening the page
  // used to leave an incomplete PaymentIntent (and customer) in Stripe on every
  // load. The Payment Element is rendered in deferred mode instead and the
  // PaymentIntent is created from createPaymentIntent() when the customer pays.
  useEffect(() => {
    let mounted = true;
    const fetchCheckoutPricing = async () => {
      setIsLoading(true);
      try {
        const resp = await axios.post(`${BASE_URL}/checkout/init-checkout`, {
          shippingAmount:shipping,
          slug: slug,
          sid: sid || undefined
        });
        if (!mounted) return;
        const data = resp.data || {};
        if (data.success === false) {
          setSessionError(data.error || 'Something went wrong. Please try again.');
          return;
        }
        if (data.currency) setCurrency(data.currency);
        if (data.automatic_tax) setTaxes(data.automatic_tax);
        if (data.baseAmount) {
          setBaseProduct(data.baseAmount);
          setOriginalBaseProduct(data.baseAmount);
        }
        // Initial total from the backend = baseAmount + shipping (no taxes yet),
        // or just shipping for the free product tiers.
        if (data.totalAmount) setTotal(data.totalAmount);
        if (data.subAmount) {
          setSubProduct(data.subAmount);
          setOriginalSubProduct(data.subAmount);
        }
        // Store pricing tier and original prices for order summary
        if (data.pricingTier) setPricingTier(data.pricingTier);
        if (data.originalMainProductPrice) setOriginalMainPrice(data.originalMainProductPrice);
        if (data.originalSubProductPrice) setOriginalSubPrice(data.originalSubProductPrice);
        if (data.mainPriceProductInfo) setMainPriceProductInfo(data.mainPriceProductInfo);
          setHasPromotekitReferral(Boolean(data.promotekitReferral));
          setMigratedCust(data.migratedCust || null);
          setCheckoutReady(true);

      } catch (err) {
        console.error('Failed to load checkout pricing', err);
        const serverMsg = err?.response?.data?.error;
        setSessionError(serverMsg || 'Something went wrong. Please try again.');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchCheckoutPricing();
    return () => { mounted = false; };
  }, [sid]);

  // Create the PaymentIntent with the final amount. Called from CheckoutForm
  // when the customer submits the payment, i.e. the only point at which a
  // PaymentIntent is created.
  const createPaymentIntent = useCallback(async ({ phone, email } = {}) => {
    const appliedPromoCodes = appliedCouponsRef.current.map(c => c.code);
    try {
      setIsLoading(true);
      const resp = await axios.post(`${BASE_URL}/checkout/create-intent`, {
        ...buildOrderPayload(appliedPromoCodes),
        phone: phone || undefined,
        // The customer this intent is for. The backend puts it on the Stripe
        // customer it creates, so the record is identifiable even if the
        // payment then fails and no webhook ever runs.
        email: email || undefined,
        // The total the customer was shown. The backend refuses to create the
        // intent if it recalculates a different total.
        expectedAmount: total,
      });
      const data = resp.data || {};
      return { success: true, clientSecret: data.clientSecret, data };
    } catch (err) {
      const data = err?.response?.data || {};
      // The order total changed between the last quote and the payment: refresh
      // the summary so the customer can review it before paying.
      if (data.code === "AMOUNT_CHANGED") {
        if (data.amount) setTotal(data.amount);
        if (data.automatic_tax !== undefined) setTaxes(data.automatic_tax);
        if (data.discountedBaseAmount) setBaseProduct(data.discountedBaseAmount);
      }
      console.error('Failed to create payment intent', err);
      return {
        success: false,
        error: data.error || err.message,
        message: data.message || 'Something went wrong. Please try again.',
      };
    } finally {
      setIsLoading(false);
    }
  }, [buildOrderPayload, total]);

  // Deferred Elements: the Payment Element is rendered from the amount/currency
  // alone, so no PaymentIntent (and no client secret) is needed to show it.
  const options = currency && total > 0
    ? {
        mode: 'payment',
        amount: total,
        currency,
        setupFutureUsage: 'off_session',
        appearance: { theme: 'stripe' },
      }
    : undefined;

  /* ---------------- UI ---------------- */
  if (sessionError) {
    return (
      <>
        <TopBar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
          <div className="text-5xl mb-4">😞</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Oops, something went wrong</h1>
          <p className="text-gray-500 max-w-md">{sessionError}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <LoaderOverlay show={isLoading} />
      {isQuoting && !isLoading && (
        <div className="pointer-events-none fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-full bg-gray-900/90 px-4 py-2 text-sm text-white shadow-lg">
          Updating your total…
        </div>
      )}
      <TopBar />

      <div className="max-w-6xl mx-auto grid md:grid-cols-[1.7fr_1.3fr] gap-10 px-4 py-8">
        <div>
           {options && (
          <Elements options={options} stripe={stripePromise}>
            <CheckoutForm 
              onCountryChange={handleCountryChange}
              setCompleteAddress={setCompleteAddress}
              total={total}
              shipping={shipping}
              taxes={taxes}
              baseProduct={baseProduct}
              onPaymentIntentUpdated={updatePaymentIntent}
              createPaymentIntent={createPaymentIntent}
              isQuoting={isQuoting}
              defaultEmail={defaultEmail}
              urlEmailError={urlEmailError}
              pricingTier={pricingTier}
              allAgreed={allAgreed}
              setAllAgreed={setAllAgreed}
              sid={sid}
              isFreeSid={isFreeSid}
              selectedPlan={selectedPlan}
              onPlanChange={setSelectedPlan}
              migratedCust={migratedCust}
            />
          </Elements>
          )}
        </div>
        <OrderSummary 
        originalProductAmount={originalProductAmount}
        baseProduct={baseProduct}
        subProduct={subProduct}
        originalBaseProduct={originalBaseProduct}
        originalSubProduct={originalSubProduct}
        country={country}
        shipping={shipping}
        total={total}
        taxes={taxes}
        onApplyPromo={updatePaymentIntent}
        pricingTier={pricingTier}
        originalMainPrice={originalMainPrice}
        originalSubPrice={originalSubPrice}
        mainPriceProductInfo={mainPriceProductInfo}
        promotekitReferral={hasPromotekitReferral}
        skipShipping={skipShipping}
        isfreeMemberShip={isfreeMemberShip}
        isfreeMemberShipMonthy={isfreeMemberShipMonthy}
        isfreeAccessory={isfreeAccessory}
        appliedCoupons={appliedCoupons}
        migratedCust={migratedCust}
        selectedPlan={selectedPlan}
        isFreeSid={isFreeSid}
      />
      </div>
    </>
  );
}